import React from 'react';

interface PropertySupplyProps {
    localityName: string;
    projects: any[];
}

const PropertySupply: React.FC<PropertySupplyProps> = ({ localityName, projects }) => {
    
    // Calculate Overall stats
    const typeCounts: Record<string, number> = {};
    projects.forEach(p => {
        const type = p.propertyType || 'Other';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    const sortedTypes = Object.entries(typeCounts)
        .sort((a: any, b: any) => b[1] - a[1])
        .slice(0, 4);

    const maxCount = sortedTypes.length > 0 ? sortedTypes[0][1] : 1;

    // Filter projects for Apartments and Villas
    const apartments = projects.filter(p => (p.propertyType || '').toLowerCase().includes('apartment'));
    const villas = projects.filter(p => (p.propertyType || '').toLowerCase().includes('villa'));

    return (
        <div className="container mx-auto px-4 py-20 max-w-7xl">
            <div className="mb-10">
                <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold text-black uppercase tracking-tight">Property Supply</h2>
                    <i className="fa-solid fa-circle-info text-black opacity-20 text-sm cursor-help"></i>
                </div>
                <p className="text-black text-sm font-medium">See the kind of properties listed in {localityName}</p>
            </div>

            <div className="relative group overflow-hidden">
                <div className="flex gap-4 overflow-x-auto no-scrollbar pb-8 scroll-smooth">
                    
                    {/* Overall Card */}
                    <div className="min-w-[320px] bg-white border border-slate-100 rounded-xl shadow-sm p-8 flex flex-col justify-between h-[440px]">
                        <div>
                            <h3 className="text-lg font-bold text-black mb-1">Overall</h3>
                            <p className="text-[12px] font-bold text-black mb-8">Property types with most no. of listings</p>

                            <div className="space-y-6">
                                {sortedTypes.map(([type, count], i) => (
                                    <div key={type} className="flex flex-col gap-2">
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-[14px] font-black text-black"><span className="text-black mr-1 italic text-[12px]">#{i+1}</span>{type}</span>
                                            <span className="text-[12px] font-bold text-black">{count} properties</span>
                                        </div>
                                        <div className="w-full h-[6px] bg-slate-50 rounded-full overflow-hidden">
                                            <div className="h-full bg-black rounded-full" style={{ width: `${(count/maxCount)*100}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <button className="text-black font-black text-[10px] uppercase tracking-widest text-left mt-4 hover:opacity-70 transition-opacity underline">View all Properties</button>
                    </div>

                    {/* Apartments Card */}
                    <div className="min-w-[320px] bg-white border border-slate-100 rounded-xl shadow-sm p-8 flex flex-col justify-between h-[440px]">
                        <div>
                            <h3 className="text-lg font-bold text-black mb-1">Apartments</h3>
                            <p className="text-[12px] font-bold text-black mb-8">Most no. of listings in societies for Apartm...</p>

                            <div className="space-y-6">
                                {apartments.slice(0, 3).map((p, i) => (
                                    <div key={p._id} className="flex flex-col gap-2">
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-[14px] font-bold text-black leading-tight truncate max-w-[180px]"><span className="text-slate-300 mr-1 italic text-[12px]">#{i+1}</span>{p.name}</span>
                                            <span className="text-[12px] font-medium text-black">1 properties</span>
                                        </div>
                                        <div className="w-full h-[6px] bg-black rounded-full"></div>
                                    </div>
                                ))}
                                {apartments.length === 0 && <p className="text-xs italic text-black opacity-30">No apartment data available.</p>}
                            </div>
                        </div>
                        <button className="text-black font-black text-[10px] uppercase tracking-widest text-left mt-4 hover:opacity-70 transition-opacity underline">View all {apartments.length} Societies</button>
                    </div>

                    {/* Villas Card */}
                    <div className="min-w-[320px] bg-white border border-slate-100 rounded-xl shadow-sm p-8 flex flex-col justify-between h-[440px]">
                        <div>
                            <h3 className="text-lg font-bold text-black mb-1">Villas</h3>
                            <p className="text-[12px] font-bold text-black mb-8">Most no. of listings in societies for Villa...</p>

                            <div className="space-y-6">
                                {villas.slice(0, 3).map((p, i) => (
                                    <div key={p._id} className="flex flex-col gap-2">
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-[14px] font-bold text-black leading-tight truncate max-w-[180px]"><span className="text-slate-300 mr-1 italic text-[12px]">#{i+1}</span>{p.name}</span>
                                            <span className="text-[12px] font-medium text-black">1 properties</span>
                                        </div>
                                        <div className="w-full h-[6px] bg-black rounded-full"></div>
                                    </div>
                                ))}
                                {villas.length === 0 && <p className="text-xs italic text-black opacity-30">No villa data available.</p>}
                            </div>
                        </div>
                        <button className="text-black font-black text-[10px] uppercase tracking-widest text-left mt-4 hover:opacity-70 transition-opacity underline">View all {villas.length} Societies</button>
                    </div>

                </div>

                {/* Right Scroll Indicator */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white border border-slate-100 shadow-lg rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-all z-10">
                    <i className="fa-solid fa-chevron-right text-slate-600 text-[12px]"></i>
                </div>
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PropertySupply;
